import { mergeAttributes } from '@tiptap/core';
import { DocumentEmbedAttributesInterface } from '../../interfaces/tiptapInterface';
import { DOCUMENT_FILE_ICON_URL, DOCUMENT_CLOSE_ICON_URL } from '../../global/constants';
import createEmbed from './createEmbed';
import type { DOMOutputSpec } from '@tiptap/pm/model';

export interface DocumentEmbedOptions {
  HTMLAttributes?: DocumentEmbedAttributesInterface;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    documentEmbed: {
      setDocumentEmbed: (options: {
        url: string;
        documentId: string;
        documentName: string;
        documentType: string;
        documentSize: string;
        themeMode?: string;
      }) => ReturnType;
      removeDocumentEmbed: () => ReturnType;
    };
  }
}

export const DocumentEmbed = createEmbed({
  name: 'documentEmbed',
  tag: 'div.document-embed-preview-main-div',
  component: () => null, // Document embed uses custom renderHTML instead of React component
  useNodeView: false, // Don't add addNodeView - rely on renderHTML only
  selectable: true,
  draggable: false,
  attributes: {
    url: { default: '' },
    documentId: { default: '' },
    documentName: { default: '' },
    documentType: { default: '' },
    documentSize: { default: '' },
    themeMode: { default: 'light' }
  },
  parseHTMLGetAttrs: (dom: HTMLElement) => {
    return {
      url: dom.getAttribute('url'),
      documentId: dom.getAttribute('documentId'),
      documentName: dom.getAttribute('documentName') || '',
      documentType: dom.getAttribute('documentType') || '',
      documentSize: dom.getAttribute('documentSize'),
      themeMode: dom.getAttribute('themeMode')
    };
  },
  customRenderHTML: ({ HTMLAttributes }) => {
    const { documentName, documentType } = HTMLAttributes;
    const documentTypeStr = typeof documentType === 'string' ? documentType : '';

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        class: 'document-embed-preview-main-div',
        url: HTMLAttributes?.url,
        documentId: HTMLAttributes?.documentId,
        documentName: HTMLAttributes?.documentName || '',
        documentType: HTMLAttributes?.documentType || '',
        documentSize: HTMLAttributes?.documentSize,
        themeMode: HTMLAttributes?.themeMode
      }),
      [
        'div',
        { class: 'document-embed-container' },
        [
          'div',
          { class: 'document-embed-icon' },
          [
            'img',
            {
              src: DOCUMENT_FILE_ICON_URL,
              width: '20',
              height: '28',
              class: 'document-embed-icon-image'
            }
          ]
        ],
        [
          'div',
          { class: 'document-content' },
          ['div', { class: 'file-name' }, documentName || 'Untitled Document'],
          ['div', { class: 'file-type' }, `${documentTypeStr.toUpperCase()}`]
        ],
        [
          'div',
          {
            class: 'close-icon',
            'data-command': 'removeDocumentEmbed'
          },
          [
            'img',
            {
              src: DOCUMENT_CLOSE_ICON_URL,
              width: '14',
              height: '14',
              class: 'close-icon-image'
            }
          ]
        ]
      ]
    ] as DOMOutputSpec;
  },
  extraCommands: {
    removeDocumentEmbed:
      (embedName: string) =>
      ({
        commands,
        state
      }: {
        commands: { deleteNode: (name: string) => boolean };
        state: { selection: { $from: { node: () => { type: { name: string } } } } };
      }) => {
        const { selection } = state;
        const { $from } = selection;
        const node = $from.node();

        if (node.type.name === embedName) {
          return commands.deleteNode(embedName);
        }
        return false;
      }
  }
});
